import type { Feature } from 'geojson'
import { atom } from 'jotai'
import { atomWithLocation } from 'jotai-location'
import { atomWithStorage } from 'jotai/utils'
import type { Sort } from 'mongodb'
import { type LngLatBounds, type MapRef } from 'react-map-gl'
import { defaultFilters } from '~/constants'
import { Listing } from '~/models'

export const aFiltersIsOpen = atom<boolean>(false)
export const aLayersIsOpen = atom<boolean>(false)
export const aMapRef = atom<MapRef | null>(null)
export type MapStyleValue = 'street' | 'satellite'
export const aMapStyle = atomWithStorage<'street' | 'satellite'>(
  'mapStyle',
  'street'
)

export const MapStyles: { [key: string]: string } = {
  street: 'mapbox://styles/mapbox/streets-v11',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
}

export const aMapViewState = atom<{
  bounds: LngLatBounds
  lat: number
  lng: number
  zoom: number
} | null>(null)

export const aFiltersPinned = atomWithStorage<boolean>(
  'mapFiltersPinned',
  false
)

export const aMapCurrentCount = atom<number | null>(null)
export const aCheckedListings = atom<string[]>([])

export const aDrawnFeature = atom<Feature | null>(null)
export const aVisibleLayers = atom<string[]>([])

export const aParcelDataVisible = atomWithStorage<boolean>(
  'parcelDataVisible',
  false
)

export const aVisibleGeographies = atomWithStorage<string[]>(
  'visibleGeographies',
  []
)
export type SelectedListing = {
  _id: string
  slug: string
  status: string
  type: string
  property_types: string[]
  location: {
    coordinates: number[]
  }
}

export const aSelectedListing = atom<SelectedListing | Listing | null>(null)

export type HoverFeature = {
  address?: string
  lat: number
  lng: number
  name: string
  property_types: string
  status: string
  type: string
  _id: string
  card?: boolean
}
export const aHoverFeature = atom<HoverFeature | null>(null)
export const aMarkLocation = atom<boolean>(false)
export const aIsMapEasing = atom<boolean>(false)

export const aDrawMode = atom<string | null>(null)

const aMapLocation = atomWithLocation()

export type MapFiltersType = {
  lat?: string
  lng?: string
  z?: string
  b?: string
  p?: string
  types?: string[]
  statuses?: string[]
  property_types?: string[]
  max_price?: number
  min_price?: number
  max_cap?: number
  min_cap?: number
  max_rate?: number
  min_rate?: number
  max_acres?: number
  min_acres?: number
  min_sf?: number
  max_sf?: number
  min_bsf?: number
  max_bsf?: number
  cities?: string[]
  counties?: string[]
  zipCodes?: string[]
  sort?: string
  featured?: 'true'
  labels?: string[]
}

const URL_VAR_DELIMITER = ','

export const aMapFilters = atom(
  (get): MapFiltersType => {
    const defaultMapFilters = { ...defaultFilters }
    const filters: { [key: string]: any } = {}
    let searchParams =
      get(aMapLocation).searchParams && get(aMapLocation).searchParams!.size > 0
        ? get(aMapLocation).searchParams
        : null
    if (!searchParams && typeof window !== 'undefined') {
      searchParams = new URLSearchParams(window.location.search)
    }
    if (searchParams) {
      for (const [key, value] of searchParams) {
        if (
          ['lng', 'lat', 'z', 'b', 'p', 'sort', 'featured'].includes(key) &&
          value !== '0'
        ) {
          filters[key] = value
        } else if (
          [
            'max_price',
            'min_price',
            'max_cap',
            'min_cap',
            'max_rate',
            'min_rate',
            'max_acres',
            'min_acres',
            'min_sf',
            'max_sf',
            'min_bsf',
            'max_bsf',
          ].includes(key) &&
          value !== '0'
        ) {
          const parsed = parseFloat(value)
          filters[key] = Number.isNaN(parsed) ? undefined : parsed
        } else {
          filters[key] = value
            .split(URL_VAR_DELIMITER)
            .map((v) => {
              const parsed = parseFloat(v)
              return Number.isNaN(parsed) ? v : parsed
            })
            .filter((v) => v)
        }
      }
      return {
        ...defaultMapFilters,
        ...filters,
      }
    }
    return defaultMapFilters
  },
  (get, set, filters: { [key: string]: any }) => {
    const params = getParamsFromMapFilters(filters)
    const selectedListing = get(aSelectedListing)
    set(aMapLocation, {
      pathname: selectedListing ? `/${selectedListing.slug}` : '/',
      searchParams: new URLSearchParams(params),
    })
  }
)

function getParamsFromMapFilters(filters: { [key: string]: any }) {
  const params: [string, string][] = []
  // Compare filters to default and exclude if the same
  Object.keys(filters).forEach((key) => {
    if (filters[key] && !['lat', 'lng', 'z', 'b', 'p'].includes(key)) {
      if (Array.isArray(filters[key])) {
        if (filters[key].filter((v?: any) => v).length > 0) {
          params.push([key, filters[key].join(URL_VAR_DELIMITER)])
        }
      } else {
        params.push([key, filters[key]])
      }
    }
  })
  if (typeof filters.b !== 'undefined' && filters.b != 0) {
    params.unshift(['b', filters.b])
  }
  if (typeof filters.p !== 'undefined' && filters.p != 0) {
    params.unshift(['p', filters.p])
  }
  if (typeof filters.z !== 'undefined') {
    params.unshift(['z', filters.z])
  }
  if (
    typeof filters.lat !== 'undefined' &&
    typeof filters.lng !== 'undefined'
  ) {
    params.unshift(['lat', filters.lat])
    params.unshift(['lng', filters.lng])
  }
  return params
}

type $in<T = string> = { $in: T[] }

type NumberRange =
  | { $lte: number; $gte?: number }
  | { $gte: number; $lte?: number }

type MinMaxRange<MinKey extends string, MaxKey extends string, T = number> =
  | { [K in MaxKey]: { $gte: T } }
  | { [K in MinKey]: { $lte: T } }
  | {
      $or: [
        {
          $and: [{ [K in MinKey]: { $gte: T } }, { [K in MinKey]: { $lte: T } }]
        },
        {
          $and: [{ [K in MaxKey]: { $gte: T } }, { [K in MaxKey]: { $lte: T } }]
        },
        {
          $and: [{ [K in MinKey]: { $lte: T } }, { [K in MaxKey]: { $gte: T } }]
        },
      ]
    }

type MapFiltersLeaseRateFind = MinMaxRange<
  'meta.min_lease_rate',
  'meta.max_lease_rate'
>

type MapFiltersLeaseAvailableSfFind = MinMaxRange<
  'meta.min_available_sf',
  'meta.max_available_sf'
>

type MapFiltersLeaseFind = {
  // lease, sale/lease
  type: { $in: string[] }
  $and?: (MapFiltersLeaseRateFind | MapFiltersLeaseAvailableSfFind)[]
}

type MapFiltersSaleFind = {
  type: { $in: string[] }
  'meta.price'?: NumberRange
  'meta.cap_rate'?: NumberRange
}

type MapFiltersTypesFind =
  | MinMaxRange<'meta.min_acres', 'meta.max_acres'>
  | {
      $or: (MapFiltersSaleFind | MapFiltersLeaseFind)[]
    }

type MapFiltersFind = {
  status?: { $in: string[] }
  property_types?: { $in: string[] }
  $and: MapFiltersTypesFind[]
  'meta.building_sf'?: NumberRange
  'meta.cap_rate'?: NumberRange
  'property.city'?: $in
  'property.county'?: $in
  'property.zip_code'?: $in
  labels?: string[]
  featured?: 'true'
  'meta.min_acres'?: { $ne: null }
  'meta.max_acres'?: { $ne: null }
  'meta.min_available_sf'?: { $ne: null }
  'meta.max_available_sf'?: { $ne: null }
  'meta.min_lease_rate'?: { $ne: null }
  'meta.max_lease_rate'?: { $ne: null }
  'meta.price'?: { $ne: null }
}

function getNumberRange(min?: number, max?: number): NumberRange | undefined {
  const range: Partial<NumberRange> = {}

  if (min) {
    range.$gte = min
  }

  if (max) {
    range.$lte = max
  }

  return min === undefined && max === undefined
    ? undefined
    : (range as NumberRange)
}

function getMinMaxRange<MinKey extends string, MaxKey extends string>(
  minKey: MinKey,
  maxKey: MaxKey,
  min?: number,
  max?: number
): MinMaxRange<MinKey, MaxKey> | undefined {
  if (min !== undefined && max !== undefined) {
    return {
      $or: [
        {
          $and: [{ [minKey]: { $gte: min } }, { [minKey]: { $lte: max } }],
        },
        {
          $and: [{ [maxKey]: { $gte: min } }, { [maxKey]: { $lte: max } }],
        },
        {
          $and: [{ [minKey]: { $lte: min } }, { [maxKey]: { $gte: max } }],
        },
      ],
    } as MinMaxRange<MinKey, MaxKey>
  } else if (min) {
    return { [maxKey]: { $gte: min } } as MinMaxRange<MinKey, MaxKey>
  } else if (max) {
    return { [minKey]: { $lte: max } } as MinMaxRange<MinKey, MaxKey>
  }
}

// function to get the filters for sale and lease
function getSaleLeaseFilters(filters: MapFiltersType) {
  if (!filters.types || filters.types.length === 0) {
    return []
  }

  const $or = []

  if (filters.types.includes('sale')) {
    const priceRange = getNumberRange(filters.min_price, filters.max_price)

    const sale: MapFiltersSaleFind = {
      type: { $in: ['sale', 'sale/lease'] },
      'meta.price': priceRange,
    }

    if (filters.min_cap !== undefined || filters.max_cap !== undefined) {
      sale['meta.cap_rate'] = getNumberRange(filters.min_cap, filters.max_cap)
    }

    $or.push(sale)
  }

  if (filters.types.includes('lease')) {
    const $and: MapFiltersLeaseFind['$and'] = []

    if (filters.min_rate !== undefined || filters.max_rate !== undefined) {
      const range = getMinMaxRange(
        'meta.min_lease_rate',
        'meta.max_lease_rate',
        filters.min_rate,
        filters.max_rate
      )

      if (range) {
        $and.push(range)
      }
    }

    if (filters.min_sf !== undefined || filters.max_sf !== undefined) {
      const range = getMinMaxRange(
        'meta.min_available_sf',
        'meta.max_available_sf',
        filters.min_sf,
        filters.max_sf
      )

      if (range) {
        $and.push(range)
      }
    }

    const lease: MapFiltersLeaseFind = {
      type: { $in: ['lease', 'sale/lease'] },
    }

    if ($and.length > 0) {
      lease.$and = $and
    }

    $or.push(lease)
  }

  return $or
}

function getFindFromFilters(filters: MapFiltersType) {
  const $and: MapFiltersTypesFind[] = [
    { $or: getSaleLeaseFilters(filters) }, // lease and sale specific
  ]

  if (filters.min_acres !== undefined || filters.max_acres !== undefined) {
    const range = getMinMaxRange(
      'meta.min_acres',
      'meta.max_acres',
      filters.min_acres,
      filters.max_acres
    )

    if (range) {
      $and.push(range)
    }
  }

  const find: MapFiltersFind = {
    status: { $in: filters.statuses! },
    $and,
  }

  if ((filters.property_types || []).length > 0) {
    find.property_types = { $in: filters.property_types! }
  }

  if (filters.min_bsf !== undefined || filters.max_bsf !== undefined) {
    find['meta.building_sf'] = getNumberRange(filters.min_bsf, filters.max_bsf)
  }

  if (filters.cities && filters.cities.length > 0) {
    find['property.city'] = { $in: filters.cities }
  }

  if (filters.counties && filters.counties.length > 0) {
    find['property.county'] = { $in: filters.counties }
  }

  if (filters.zipCodes && filters.zipCodes.length > 0) {
    find['property.zip_code'] = {
      $in: filters.zipCodes.map((zip) => zip.toString()),
    }
  }

  if (filters.labels && filters.labels.length > 0) {
    find['labels'] = filters.labels
  }

  if (filters.featured === 'true') {
    find['featured'] = 'true'
  }

  if (filters.sort) {
    if (filters.sort.includes('price')) {
      find['meta.price'] = { $ne: null }
    } else if (filters.sort.includes('rateD')) {
      find['meta.max_lease_rate'] = { $ne: null }
    } else if (filters.sort.includes('rateA')) {
      find['meta.min_lease_rate'] = { $ne: null }
    } else if (filters.sort === 'availableSfD') {
      find['meta.max_available_sf'] = { $ne: null }
    } else if (filters.sort === 'availableSfA') {
      find['meta.min_available_sf'] = { $ne: null }
    } else if (filters.sort.includes('acresD')) {
      find['meta.max_acres'] = { $ne: null }
    } else if (filters.sort.includes('acresA')) {
      find['meta.min_acres'] = { $ne: null }
    }
  }

  return find
}

function getSortFromFilters(filters: MapFiltersType) {
  let sort: Sort = { modified: -1 }

  const sortKeys: { [key: string]: string } = {
    modifiedD: 'modified',
    createdD: 'created',
    availableSfD: 'meta.max_available_sf',
    availableSfA: 'meta.min_available_sf',
    priceD: 'meta.price',
    priceA: 'meta.price',
    rateD: 'meta.max_lease_rate',
    rateA: 'meta.min_lease_rate',
    acresD: 'meta.max_acres',
    acresA: 'meta.min_acres',
  }

  if (filters.sort) {
    const direction = filters.sort.slice(filters.sort.length - 1)

    if (
      typeof sortKeys[filters.sort] !== 'undefined' &&
      (direction === 'D' || direction === 'A')
    ) {
      sort = { [sortKeys[filters.sort]]: direction === 'D' ? -1 : 1 }
    }
  }

  return sort
}

export const aFiltersFind = atom((get) => {
  const filters = get(aMapFilters)
  return getFindFromFilters(filters)
})

export const aFiltersSort = atom((get) => {
  const filters = get(aMapFilters)
  return getSortFromFilters(filters)
})
